/**
 * IncompleteRunNotice — shown next to a run's rates when the run is
 * incomplete (its excluded + failed calls exceed the backend threshold; see
 * backend app/services/run_completeness.py). Says that the rates are over the
 * answered calls and lists the count by lane. Renders nothing for a complete
 * run, so a run under the threshold looks exactly as it did before.
 *
 *   variant="banner" — full sentence plus one line per lane (headline areas)
 *   variant="chip"   — compact badge, lanes in its tooltip (lists, pickers)
 *
 * `children` (banner only) renders at the foot of the notice: the run card
 * puts the "Re-collect unanswered calls" action there.
 */
import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

import { isIncomplete, type RunCompleteness } from "@/api/run-completeness";
import { PROVIDER_LABEL, type ProviderId } from "@/app/constants/models";

function laneLabel(lane: string): string {
  return PROVIDER_LABEL[lane as ProviderId] ?? lane;
}

export function laneSummaries(completeness: RunCompleteness): string[] {
  return Object.entries(completeness.by_lane).map(
    ([lane, c]) =>
      `${laneLabel(lane)}: ${c.unmeasured} of ${c.calls} calls not measured ` +
      `(${c.excluded_no_answer} no answer, ${c.failed_zeroed} failed scoring)`,
  );
}

export function incompleteRunSentence(completeness: RunCompleteness): string {
  const share = (completeness.unmeasured_share * 100).toFixed(1);
  return (
    `Incomplete run: ${completeness.unmeasured} of ${completeness.calls} calls ` +
    `(${share}%) were not measured. Rates are over the ${completeness.answered} answered calls.`
  );
}

export function IncompleteRunNotice({
  completeness,
  variant = "banner",
  children,
}: {
  completeness: RunCompleteness | null | undefined;
  variant?: "banner" | "chip";
  children?: ReactNode;
}) {
  if (!isIncomplete(completeness)) return null;
  const lanes = laneSummaries(completeness);

  if (variant === "chip") {
    return (
      <span
        title={[incompleteRunSentence(completeness), ...lanes].join("\n")}
        className="inline-flex items-center gap-1"
        style={{
          fontSize: 11, fontWeight: 600, padding: "1px 8px", borderRadius: 999,
          color: "var(--warning-text)", background: "var(--warning-bg)",
          border: "1px solid var(--warning-border)", whiteSpace: "nowrap",
        }}
      >
        <AlertTriangle className="w-3 h-3" />
        Incomplete · {completeness.unmeasured}/{completeness.calls} unmeasured
      </span>
    );
  }

  return (
    <div
      role="note"
      style={{
        marginTop: 8, padding: "8px 12px", borderRadius: 8,
        color: "var(--warning-text)", background: "var(--warning-bg)",
        border: "1px solid var(--warning-border)", fontSize: 12, lineHeight: 1.5,
      }}
    >
      <div className="flex items-center gap-1.5" style={{ fontWeight: 600 }}>
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
        {incompleteRunSentence(completeness)}
      </div>
      {lanes.length > 0 && (
        <ul style={{ margin: "4px 0 0 20px", listStyle: "disc" }}>
          {lanes.map((line) => <li key={line}>{line}</li>)}
        </ul>
      )}
      {children}
    </div>
  );
}
